/**
 * R30 V3 — the model-aware jitter-buffer depth map.
 *
 * ── Why this exists ────────────────────────────────────────────────────────────────────────────
 * The audio jitter buffer (the AudioWorklet pre-roll in
 * `patches/@pipecat-ai__websocket-transport@1.6.7.patch`) was tuned to 683ms on `eleven_flash_v2_5`
 * (dev-64 `?audiodebug`, the audio-rendering campaign). But R53 (§M1) ships a per-language TTS model
 * map — default `eleven_turbo_v2_5`, `en → turbo`, every other language → `eleven_multilingual_v2` —
 * and turbo/v2 chunk-gap distributions are 2–3.4× flash's, so the flash-tuned 683ms buffer underruns
 * on them (the dev-117 retest pair suffered buffer-amplified dropouts). The correct lever is a DEEPER
 * client buffer on the slower-streaming models, applied when the active model is known.
 *
 * This module is the config MAP (not magic numbers): model id → pre-buffer depth. The worklet is
 * parameterised by a frame count (128-sample quanta at 24 kHz); this module converts ms → frames so
 * the depths are expressed in the perceptual unit (ms) and the frame math lives in one place.
 *
 * ── The depths (starting points — concierge R56's B1 A/B may refine) ────────────────────────────
 *   flash (`eleven_flash_v2_5`)         → 683ms  (UNCHANGED — the campaign-proven flash depth)
 *   turbo (`eleven_turbo_v2_5`)         → 1100ms (the shipped default; ~1.6× flash)
 *   v2    (`eleven_multilingual_v2`)    → 1400ms (~2× flash — the deepest, for the multilingual model)
 *   unknown/default                     → 1100ms (turbo — the ship default, so an unrecognised model
 *                                                 is never LESS buffered than the common path)
 *
 * ⚠️ BINDING STATUS (R30): the depths are STARTING POINTS. concierge R56's B1 TTS-pacing A/B may
 * refine them; coordinate via the fixture/report and update `MODEL_JITTER_MS`. The model→ms map is
 * the single seam. The ACTIVE-TTS-MODEL EMIT that tells the client which model is live is concierge
 * R56's new wire — not landed at R30 authoring time — so `parseActiveTtsModel` reads a tolerant shape
 * and the binding is UNVERIFIED against a captured fixture (cross-verify the frozen `ui.tts_model`
 * envelope at integration).
 */

/** The TTS models the stack runs (ids from `packages/shared-types/src/supported-languages.json`). */
export type TtsModel = 'eleven_flash_v2_5' | 'eleven_turbo_v2_5' | 'eleven_multilingual_v2';

/** The worklet runs 24 kHz, 128-sample quanta — one "frame" = 128 samples ≈ 5.333ms. */
export const WORKLET_SAMPLE_RATE = 24_000;
export const WORKLET_QUANTUM_SAMPLES = 128;

/**
 * Per-model pre-buffer TARGET depth, in MILLISECONDS (the perceptual unit). Config, not magic
 * numbers — one place to tune from concierge R56's A/B. flash is the campaign-proven 683ms
 * (unchanged). This is the depth the buffer GROWS toward while playing (the ceiling of the R31
 * warm-up ramp), NOT the depth it waits for before first playback (that is `PREROLL_FLOOR_MS`).
 */
export const MODEL_JITTER_MS: Record<TtsModel, number> = {
  eleven_flash_v2_5: 683,
  eleven_turbo_v2_5: 1_100,
  eleven_multilingual_v2: 1_400,
};

/** Fallback TARGET depth when the active model is unknown/unrecognised — the shipped default
 *  (turbo). Never LESS buffered than the common path, so an unknown model can't regress to the
 *  shallow flash depth. */
export const DEFAULT_JITTER_MS = MODEL_JITTER_MS.eleven_turbo_v2_5;

/**
 * R31 — the ADAPTIVE PRE-ROLL FLOOR (ms). The single lever that recovers the turn-start latency the
 * ui-R30 model-aware buffer re-introduced.
 *
 * ── Why this exists ────────────────────────────────────────────────────────────────────────────
 * ui-R30 deepened the pre-buffer to 683/1100/1400ms so the SLOWER-streaming models stopped
 * underrunning mid-speech (dev-118: zero garbling complaints, first session ever). But the worklet
 * waited for the FULL per-model target before the FIRST sample played — so on turbo/v2 the ~1.1–1.4s
 * fill landed ON TOP of the ~4.7s server TTFT at every turn start (~6s of silence; the visitor felt
 * "long pauses between every answer"). The mid-speech smoothness came at a turn-START cost.
 *
 * The fix is a WARM-UP RAMP: begin playback as soon as a small FLOOR (~450ms) is buffered, then let
 * the buffer keep GROWING toward the per-model target WHILE PLAYING (fill-while-playing). The floor
 * is the perceived turn-start latency; the target is the mid-speech jitter budget. The two are now
 * decoupled — start fast, stay deep. On a warm-up underrun the worklet re-fills toward the TARGET
 * (never a hard fallback to the old wait-for-full-target behaviour) — graceful, no click.
 *
 * ── Why 450ms ──────────────────────────────────────────────────────────────────────────────────
 * The pre-R30 flash-tuned depth was 683ms (256ms was too shallow — chronic starvation, dev-64). 450
 * sits below every model's target (so the ramp always has headroom to grow) yet is deep enough to
 * absorb a normal first-chunk-burst gap. It is imperceptible against the multi-second warm TTFT while
 * shaving ~250–950ms off the OLD wait-for-full-target turn start. The floor is CLAMPED to never
 * exceed the resolved target (`preRollForModel` — a hypothetical sub-floor target, e.g. a future fast
 * model tuned below 450ms, collapses the ramp to floor=target with no negative headroom).
 */
export const PREROLL_FLOOR_MS = 450;

/** Convert a pre-buffer depth in ms to worklet frames (128-sample quanta at 24 kHz). Rounded. */
export function msToFrames(ms: number): number {
  return Math.max(1, Math.round((ms * WORKLET_SAMPLE_RATE) / 1000 / WORKLET_QUANTUM_SAMPLES));
}

/** The pre-buffer TARGET depth in ms for a model id (tolerant of a null/unknown id → the default). */
export function jitterMsForModel(model: string | null | undefined): number {
  if (model && model in MODEL_JITTER_MS) {
    return MODEL_JITTER_MS[model as TtsModel];
  }
  return DEFAULT_JITTER_MS;
}

/** The pre-buffer TARGET depth in worklet FRAMES for a model id — the ramp ceiling (the depth the
 *  buffer grows toward while playing, and re-fills toward on a warm-up underrun). This is the value
 *  the worklet's `prebuffer` message carries as `targetFrames`. */
export function preBufferFramesForModel(model: string | null | undefined): number {
  return msToFrames(jitterMsForModel(model));
}

/**
 * R31 — the warm-up ramp for a model id: the FLOOR (frames buffered before first playback) and the
 * TARGET (frames the buffer grows toward while playing). The floor is `PREROLL_FLOOR_MS` CLAMPED so
 * it never exceeds the model's target — a target already ≤ the floor (a hypothetical fast future
 * model tuned below 450ms) collapses the ramp to floor = target (a plain wait-for-target, no
 * negative headroom, never a floor DEEPER than the target). Pure so the ramp math is unit-testable.
 */
export function preRollForModel(model: string | null | undefined): {
  floorFrames: number;
  targetFrames: number;
} {
  const targetFrames = preBufferFramesForModel(model);
  const floorFrames = Math.min(msToFrames(PREROLL_FLOOR_MS), targetFrames);
  return { floorFrames, targetFrames };
}

/**
 * The global hook the vendored transport patch publishes so the MAIN thread can set the worklet's
 * warm-up ramp. Channels (all owned by the patch — see
 * `patches/@pipecat-ai__websocket-transport@1.6.7.patch`):
 *   - `floorFrames`: the pre-roll FLOOR the worklet buffers before first playback (R31 warm-up
 *     start), read at CONSTRUCTION (`_start()` mints a fresh AudioWorkletNode per utterance, so a
 *     per-session ramp set BEFORE the first utterance is honoured by every worklet the session
 *     creates);
 *   - `targetFrames`: the ramp CEILING the worklet grows toward while playing (and re-fills toward on
 *     a warm-up underrun), also read at construction;
 *   - `frames`: LEGACY alias, kept in lock-step with `targetFrames` so a not-yet-patched worklet
 *     (the READ-side ramp is an Andy-applied pnpm-patch delta) still buffers to the R30 model-aware
 *     depth rather than regressing to the shallow flash default — graceful degradation, never a
 *     shallower buffer than R30 while the two halves are out of sync;
 *   - `port`: a reference to the LIVE worklet node's MessagePort (published by `_start()` when it
 *     connects the node) so a MID-SESSION ramp change (the set-language model switch) reaches the
 *     currently-playing worklet immediately via
 *     `postMessage({ event:'prebuffer', floorFrames, targetFrames, frames })`.
 * READ-ONLY from our side: we only SET the frame fields and POST to `port`; the patch owns
 * publishing `port` + reading the fields inside the worklet.
 */
type PreBufferHook = {
  floorFrames?: number;
  targetFrames?: number;
  frames?: number;
  port?: { postMessage: (msg: unknown) => void };
};

function preBufferHook(): PreBufferHook {
  const g = globalThis as { __coldpressPreBuffer?: PreBufferHook };
  if (!g.__coldpressPreBuffer) g.__coldpressPreBuffer = {};
  return g.__coldpressPreBuffer;
}

/**
 * Apply a warm-up ramp (FLOOR + TARGET, in FRAMES) to the audio worklet. Sets the per-session
 * default the next worklet reads at construction AND, if a worklet is already live (mid-utterance),
 * pushes the ramp to its port so the change takes effect immediately (the set-language model switch
 * mid-session re-runs the SAME warm-up). No-op-safe when the patch hasn't published a live port yet
 * (the default still lands on the next `_start()`). The legacy `frames` field tracks `targetFrames`
 * so a not-yet-patched worklet keeps the R30 depth. Pure aside from the global hook + the port post.
 */
export function applyPreRoll(floorFrames: number, targetFrames: number): void {
  const hook = preBufferHook();
  hook.floorFrames = floorFrames;
  hook.targetFrames = targetFrames;
  hook.frames = targetFrames; // legacy alias — never regress an un-patched worklet below R30 depth
  try {
    hook.port?.postMessage({ event: 'prebuffer', floorFrames, targetFrames, frames: targetFrames });
  } catch {
    // A stale/closed port (the worklet was torn down between publish + post) — the default on the
    // hook still lands on the next worklet; never throw into the caller's audio/session path.
  }
}

/**
 * DEPRECATED shim — apply a single depth as a degenerate ramp (floor = target = `frames`). Retained
 * so any caller / test that still speaks the pre-R31 single-depth API keeps working; new call sites
 * use `applyPreRoll` / `applyPreBufferForModel`. Sets the same hook fields `applyPreRoll` does.
 */
export function applyPreBufferFrames(frames: number): void {
  applyPreRoll(frames, frames);
}

/**
 * Convenience: resolve + apply the warm-up ramp for a model id in one call (session start / a
 * mid-session model change / the set-language switch). Returns the TARGET frames applied (the ramp
 * ceiling — the stable single-number identity callers log; the floor is derivable via
 * `preRollForModel`). Applies the FULL ramp (floor + target) to the worklet under the hood.
 */
export function applyPreBufferForModel(model: string | null | undefined): number {
  const ramp = preRollForModel(model);
  applyPreRoll(ramp.floorFrames, ramp.targetFrames);
  return ramp.targetFrames;
}

/**
 * Parse the ACTIVE-TTS-MODEL id from a raw server message. **FROZEN wire (concierge R56 §41):** the
 * model rides `tts_model` at the TOP LEVEL of the `ui.session` message — BOOT
 * `{type:'ui.session', conversation_id, recording, tts_model}` + REMAP `{type:'ui.session', tts_model}`
 * (captured fixture `tests/fixtures/wire/r56-ui-session-tts-model.json`). There is NO
 * `ui.tts_model`/`ui.model` message type — the CALLER gates on `ui.session` and feeds the whole
 * message here. This reads `tts_model` (also tolerant of a `model` key + a nested payload/data
 * envelope, belt-and-braces against a future re-nest), returning the first non-empty string, else
 * null. An absent/null `tts_model` → null → the caller keeps its default jitter buffer (graceful).
 * Pure so it's unit-testable against the captured fixture.
 */
export function parseActiveTtsModel(message: unknown): string | null {
  if (!message || typeof message !== 'object') return null;
  const m = message as { model?: unknown; tts_model?: unknown; payload?: unknown; data?: unknown };
  const fromLevel = (v: unknown): string | null => {
    if (!v || typeof v !== 'object') return null;
    const o = v as { model?: unknown; tts_model?: unknown };
    if (typeof o.model === 'string' && o.model.trim() !== '') return o.model.trim();
    if (typeof o.tts_model === 'string' && o.tts_model.trim() !== '') return o.tts_model.trim();
    return null;
  };
  return fromLevel(m) ?? fromLevel(m.payload) ?? fromLevel(m.data);
}
